import { useEffect, useState } from 'react';
import { Sparkles, Copy, Check, MessageCircle, Mail, Twitter, Send, Users, Star } from 'lucide-react';
import api from '@/utils/api';
import { useLang } from '@/contexts/LanguageContext';

/**
 * OrbitReferralCard
 * -----------------
 * Post-purchase micro-funnel shown after a successful checkout where the
 * buyer added the Orbit Starter Pack. It:
 *  1) Confirms the reserved pre-order slot for the upcoming course.
 *  2) Issues the buyer a personal referral code/link.
 *  3) Offers one-click sharing (WhatsApp, Email, Twitter, Copy) — each friend
 *     who redeems the code gets 20% off the Starter Pack at checkout.
 *  4) Shows a 0/3 invite progress ring as the buyer shares.
 */
export default function OrbitReferralCard({ orderId }) {
  const { lang, dir } = useLang();
  const isAR = lang === 'ar';
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  const T = isAR ? {
    reservedTag: 'مقعدك محجوز',
    title: 'لقد ضمنت مقعدك في المدار القادم.',
    subtitle: 'كل صديق يستخدم رابطك يحصل على خصم 20٪ من باقة البداية.',
    codeLabel: 'رمز الإحالة الخاص بك',
    urlLabel: 'رابط المشاركة',
    copy: 'نسخ',
    copied: 'تم النسخ',
    shareOn: 'شارك عبر',
    whatsapp: 'واتساب',
    email: 'البريد',
    twitter: 'X / تويتر',
    invited: 'تمت الدعوة',
    friendsJoined: 'انضموا',
    progress: 'تقدم الدعوات',
    nudge: 'ادعُ ٣ أصدقاء — افتح خصمًا إضافيًا',
    shareText: 'أقرأ حاليًا «مركز الجاذبية» لطارق — هدية لك: خصم ٢٠٪ على باقة البداية.',
    emailSubject: 'هدية لك من مدار طارق',
  } : {
    reservedTag: 'Seat reserved',
    title: 'You\'re in — your Orbit seat is held.',
    subtitle: 'Every friend who uses your link gets 20% off the Starter Pack.',
    codeLabel: 'Your referral code',
    urlLabel: 'Share link',
    copy: 'Copy',
    copied: 'Copied',
    shareOn: 'Share via',
    whatsapp: 'WhatsApp',
    email: 'Email',
    twitter: 'X / Twitter',
    invited: 'invited',
    friendsJoined: 'joined',
    progress: 'Invite progress',
    nudge: 'Invite 3 friends — unlock a deeper thank-you.',
    shareText: 'I\'m reading "Center of Gravity" by Tariq — here\'s 20% off the Starter Pack for you.',
    emailSubject: 'A gift from Tariq\'s orbit',
  };

  useEffect(() => {
    let cancelled = false;
    api.get('/referrals/my')
      .then(({ data }) => { if (!cancelled) setData(data); })
      .catch(() => { if (!cancelled) setData(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  if (loading) {
    return (
      <div className="glass-card rounded-2xl p-6 mt-6 animate-pulse" data-testid="referral-card-loading">
        <div className="h-4 w-32 bg-sacred-gold/10 rounded mb-3" />
        <div className="h-8 w-full bg-sacred-gold/10 rounded" />
      </div>
    );
  }
  if (!data?.code) return null;

  const shareUrl = `${window.location.origin}/book?ref=${data.code}`;
  const shareLine = `${T.shareText} ${shareUrl}`;
  const invited = data.invites_sent || 0;
  const joined = data.friends_joined || 0;
  const progress = Math.min(100, Math.round((invited / 3) * 100));

  const track = (channel) => {
    api.post('/referrals/track-share', { channel })
      .then(({ data: d }) => setData(d))
      .catch(() => {});
  };

  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      track('copy');
      setTimeout(() => setCopied(false), 1800);
    } catch { /* clipboard may be blocked */ }
  };

  const openShare = (channel) => {
    let url = '';
    if (channel === 'whatsapp') {
      url = `https://wa.me/?text=${encodeURIComponent(shareLine)}`;
    } else if (channel === 'email') {
      url = `mailto:?subject=${encodeURIComponent(T.emailSubject)}&body=${encodeURIComponent(shareLine)}`;
    } else if (channel === 'twitter') {
      url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareLine)}`;
    }
    if (url) {
      window.open(url, '_blank', 'noopener,noreferrer');
      track(channel);
    }
  };

  return (
    <div
      dir={dir}
      className="glass-card rounded-2xl p-6 mt-6 relative overflow-hidden"
      data-testid="orbit-referral-card"
      style={{
        background: 'linear-gradient(140deg, rgba(200,165,92,0.08) 0%, rgba(44,24,16,0.04) 70%)',
        border: '1px solid rgba(200,165,92,0.25)',
      }}
    >
      {/* Soft aura */}
      <div
        aria-hidden
        className="pointer-events-none absolute -top-24 -right-24 w-64 h-64 rounded-full"
        style={{ background: 'radial-gradient(circle, rgba(200,165,92,0.18) 0%, transparent 70%)' }}
      />

      <div className="flex items-center gap-2 mb-3">
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-sacred-gold/15 text-sacred-gold text-[10px] font-body uppercase tracking-wider">
          <Star size={10} /> {T.reservedTag}
        </span>
      </div>

      <h3 className="text-heading font-display text-xl sm:text-2xl leading-snug mb-1" data-testid="referral-title">
        {T.title}
      </h3>
      <p className="text-text-muted font-body text-sm mb-5">{T.subtitle}</p>

      {/* Code + share link */}
      <div className="space-y-3 mb-5">
        <div>
          <label className="text-text-muted font-body text-[11px] uppercase tracking-wider">{T.codeLabel}</label>
          <div className="mt-1 px-4 py-3 rounded-xl bg-cream-white/70 border border-sacred-gold/20 font-mono text-sacred-gold text-lg tracking-wider text-center select-all" data-testid="referral-code">
            {data.code}
          </div>
        </div>
        <div>
          <label className="text-text-muted font-body text-[11px] uppercase tracking-wider">{T.urlLabel}</label>
          <div className="mt-1 flex gap-2">
            <input
              readOnly
              value={shareUrl}
              className="flex-1 px-4 py-3 rounded-xl glass-input font-body text-xs truncate"
              data-testid="referral-url"
              onFocus={(e) => e.target.select()}
            />
            <button
              type="button"
              onClick={onCopy}
              className="gold-button px-4 py-2 rounded-xl font-body text-xs inline-flex items-center gap-1.5 shrink-0"
              data-testid="referral-copy-btn"
            >
              {copied ? <Check size={14} /> : <Copy size={14} />}
              <span>{copied ? T.copied : T.copy}</span>
            </button>
          </div>
        </div>
      </div>

      {/* One-click share buttons */}
      <div className="mb-5">
        <p className="text-text-muted font-body text-[11px] uppercase tracking-wider mb-2">{T.shareOn}</p>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => openShare('whatsapp')}
            className="glass-button px-4 py-2.5 rounded-full font-body text-xs inline-flex items-center gap-2 hover:bg-accent-success/10 hover:border-accent-success/30 transition"
            data-testid="referral-share-whatsapp"
          >
            <MessageCircle size={14} /> {T.whatsapp}
          </button>
          <button
            type="button"
            onClick={() => openShare('email')}
            className="glass-button px-4 py-2.5 rounded-full font-body text-xs inline-flex items-center gap-2 hover:bg-sacred-gold/10 hover:border-sacred-gold/30 transition"
            data-testid="referral-share-email"
          >
            <Mail size={14} /> {T.email}
          </button>
          <button
            type="button"
            onClick={() => openShare('twitter')}
            className="glass-button px-4 py-2.5 rounded-full font-body text-xs inline-flex items-center gap-2 hover:bg-heading/5 hover:border-heading/30 transition"
            data-testid="referral-share-twitter"
          >
            <Twitter size={14} /> {T.twitter}
          </button>
        </div>
      </div>

      {/* Progress */}
      <div className="space-y-2">
        <div className="flex items-center justify-between text-[11px] font-body text-text-muted">
          <span className="inline-flex items-center gap-1.5">
            <Send size={11} /> {invited} {T.invited}
          </span>
          <span className="inline-flex items-center gap-1.5">
            <Users size={11} /> {joined} {T.friendsJoined}
          </span>
        </div>
        <div className="h-1.5 rounded-full bg-sacred-gold/10 overflow-hidden" data-testid="referral-progress">
          <div
            className="h-full bg-sacred-gold transition-all duration-700"
            style={{ width: `${progress}%` }}
          />
        </div>
        {invited < 3 && (
          <p className="text-sacred-gold/80 font-body text-[11px] inline-flex items-center gap-1 pt-1">
            <Sparkles size={11} /> {T.nudge}
          </p>
        )}
      </div>
    </div>
  );
}
